// API-local response shapes of the wave-2 control plane (apps/api/src/routes/{providers,
// routing,supervision,assistant,observability,infrastructure}.ts). Contract types come from
// @maestros/contracts as type-only imports; the rest mirrors the route handlers and the
// db read models they return. Optional fields are read with explicit fallbacks at call sites.
import type {
  AgentRole,
  EffectiveConfig,
  ModelTarget,
  Precision,
  ProviderConnection,
  RoutingConfig,
  RoutingConfigBody,
  RoutingConfigLayer,
  ValueOrigin,
} from "@maestros/contracts";

export type { EffectiveConfig, ModelTarget, ProviderConnection, RoutingConfig, RoutingConfigBody, RoutingConfigLayer };

export type ProviderAdapter = ProviderConnection["adapter"];
export type AuthMode = ProviderConnection["auth_mode"];
export type ConnectionTestKind = "connection" | "catalog" | "minimal_inference" | "capability" | "conformance";
export type CapabilityState = "declared" | "verified" | "failed" | "unknown";

// ---------- providers.ts ----------

/** connection_tests row (GET /v1/provider-connections/{id} → tests). */
export interface ConnectionTestRecord {
  id: string;
  target_type: string;
  target_id: string;
  kind: string;
  status: "passed" | "failed" | "skipped" | "unknown";
  failure_class: string | null;
  detail: Record<string, unknown>;
  latency_ms: number | null;
  used_tokens: boolean;
  created_by: string | null;
  created_at: string;
}

export type ProviderConnectionDetail = ProviderConnection & { tests: ConnectionTestRecord[] };

/** GET /v1/provider-connections/{id}/models */
export interface CatalogResponse {
  connection_id: string;
  catalog: "available" | "unavailable";
  reason: string | null;
  failure_class?: string | null;
  note?: string;
  fetched_at?: string;
  models: { external_model_id: string; model_target_id: string | null; state: string | null }[];
  registered: { model_target_id: string; external_model_id: string; state: string }[];
}

/** Result stored on the operation of a provider test (GET /v1/operations/{id}). */
export interface ProviderTestResult {
  test_id?: string;
  probe_id?: string | null;
  status?: "passed" | "failed" | "skipped" | "unknown";
  failure_class?: string | null;
  detail?: Record<string, unknown>;
}

export interface ModelPricing {
  currency: string;
  input_per_mtok: number | null;
  output_per_mtok: number | null;
  valid_from: string;
  source: string;
}

export interface TargetDependents {
  agent_profiles: { id: string; name: string; role: string; project_id: string | null }[];
  routing_configs: { id: string; project_id: string | null; version: number; state: string }[];
}

/** GET /v1/model-targets/{id} */
export type ModelTargetDetail = ModelTarget & {
  versions: { version: number; snapshot: Record<string, unknown>; created_by: string | null; created_at: string }[];
  probes: Record<string, unknown>[];
  dependents: TargetDependents;
};

/** PATCH /v1/model-targets/{id} */
export type ModelTargetPatchResult = ModelTarget & { renamed: boolean; previous_external_model_id: string | null; dependents: TargetDependents };

// ---------- routing.ts ----------

/** toAgentProfile (packages/db config/routing.ts). */
export interface AgentProfile {
  id: string;
  project_id: string | null;
  name: string;
  role: AgentRole;
  instructions: string;
  instructions_version: number;
  model_target_id: string | null;
  fallback_model_target_ids: string[];
  tools: string[];
  context_limit: number | null;
  output_limit: number | null;
  budget: { max_cost_cents_per_attempt?: number | null; max_calls_per_attempt?: number | null };
  concurrency: number;
  review_policy: { reviewer?: "maestro" | "critic" | "none" };
  state: "draft" | "validated" | "active" | "disabled";
  version: number;
  created_at: string;
  updated_at: string;
  /** PATCH only: explains that running attempts keep their pinned route. */
  applies_to?: string;
}

export type RoutingConfigRecord = RoutingConfig & { created_by: string | null };
export type EffectiveConfigView = EffectiveConfig & { rejected_overrides?: unknown[] };
export type EffectiveRow = EffectiveConfig["rows"][number];

export interface MaestroTargetRef {
  model_target_id: string;
  display_name: string;
  external_model_id: string;
  strong: boolean;
  state: string;
}

/** GET /v1/projects/{id}/maestro */
export interface MaestroView {
  project_id: string;
  maestro: MaestroTargetRef | null;
  substitutes: (MaestroTargetRef | null)[];
  origin: string;
  ready: boolean;
  impediment: string | null;
  epoch: number | null;
  last_review: Record<string, unknown> | null;
  routing_config_versions: { global: number | null; project: number | null };
}

/** PUT /v1/projects/{id}/maestro */
export interface MaestroPutResult {
  routing_config: RoutingConfigRecord;
  goal_runs: { id: string; maestro_epoch: number }[];
}

/** poolView (routes/routing.ts). */
export interface QuotaPoolView {
  id: string;
  name: string;
  rpm_limit: number | null;
  tpm_limit: number | null;
  concurrency_limit: number | null;
  budget_limit_cents: number | null;
  safety_margin: number;
  origin: ValueOrigin;
  confidence: number | null;
  measured_at: string | null;
  window: {
    used_requests: number;
    reserved_requests: number;
    in_flight: number;
    reset_at: string | null;
    next_slot_at?: string | null;
    waiting_tasks: number;
    effective_rpm?: number | null;
    conservative?: boolean;
    tokens_in_window?: number;
  };
  reported: { remaining_requests: number | null; remaining_tokens: number | null; origin: string; observed_at: string } | null;
  linked: { connections: { id: string; name: string; adapter: string }[]; model_targets: { id: string; display_name: string; connection_id: string }[] };
  /** Per-project share of the live window (RF-RATE-02); `project_id: null` = organization-level work. */
  projects?: { project_id: string | null; used_requests: number; reserved_requests: number; in_flight: number; estimated_tokens: number }[];
  version: number;
}

export interface QuotaPoolInput {
  name: string;
  rpm_limit: number | null;
  tpm_limit: number | null;
  concurrency_limit: number | null;
  budget_limit_cents: number | null;
  safety_margin: number;
  origin: ValueOrigin;
}

// ---------- supervision.ts ----------

export interface RuntimeServiceView {
  id: string;
  organization_id: string | null;
  service: string;
  instance_id: string;
  version: string;
  row_version: number;
  state: string;
  restart_count: number;
  last_heartbeat_at: string | null;
  started_at: string;
  scope: "global" | "organization";
  heartbeat_age_ms: number | null;
  heartbeat_stale: boolean;
  /** F08: derived from the heartbeat rule threshold (stopped = expected silence). */
  health?: "healthy" | "degraded" | "unavailable" | "stopped";
  alerts?: { id: string; rule: string; severity: AlertSeverity; state: AlertState; opened_at: string }[];
}

// ---------- Alerts (F08, RF-OBS-15) ----------

export type AlertSeverity = "info" | "low" | "medium" | "high" | "critical";
export type AlertState = "open" | "acknowledged" | "recovered";
export interface AlertView {
  id: string;
  organization_id: string | null;
  project_id: string | null;
  rule: "runner_heartbeat_lost" | "runner_crash_loop" | "telemetry_delayed";
  subject_type: string;
  subject_id: string;
  severity: AlertSeverity;
  state: AlertState;
  cause: string;
  evidence: Record<string, unknown>;
  occurrences: number;
  opened_at: string;
  last_seen_at: string;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  recovered_at: string | null;
  scope: "platform" | "organization";
}

/** POST /v1/runtime/actions (202): recorded only, never applied by the API. */
export interface RuntimeActionAccepted {
  operation_id: string;
  status: string;
  status_url: string;
  applied: boolean;
  execution: string;
  cause: string;
}

export interface TokenTotal {
  known: number;
  coverage: { known: number; total: number };
}

/** aggregateUsage (packages/domain usage.ts). */
export interface UsageSummary {
  calls: number;
  duplicates?: string[];
  nestedExcluded?: number;
  tokens: { input: TokenTotal; output: TokenTotal; cacheRead?: TokenTotal; cacheWrite?: TokenTotal; reasoning?: TokenTotal };
  cost: { billedCents: number; estimatedCents: number; unknownCostCalls: number; knownCents: number; totalCents: number | null };
  precision: Precision;
  effortMs: number;
  wallMs: number;
  openCalls: number;
}

export interface ModelCallView {
  id: string;
  sequence: number;
  role: string;
  purpose: string;
  requested_model: string;
  effective_model: string | null;
  provider: string | null;
  state: string;
  error_class: string | null;
  started_at: string | null;
  ended_at: string | null;
  latency_ms: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  token_count_method: string | null;
  cost_billed_cents: number | null;
  cost_estimated_cents: number | null;
  cost_precision: string;
  paid: boolean | null;
  created_at: string;
}

/** GET /v1/attempts/{id}/backend */
export interface AttemptBackend {
  attempt_id: string;
  task_id: string;
  state: string;
  end_reason: string | null;
  role: string;
  agent_profile_id: string | null;
  pinned_route: Record<string, unknown>;
  routing_config_version: number | null;
  epoch: number;
  supersedes_attempt_id: string | null;
  calls: ModelCallView[];
  interruptions: { call_id: string; state: string; error_class: string | null }[];
  model_divergences: { call_id: string; requested: string; effective: string }[];
  usage: UsageSummary;
  coverage: {
    tokens: { known: number; total: number };
    cost: { known_calls: number; total_calls: number };
    precision: Precision;
    usage?: UsageCoverage;
    calls_list?: { calls_included: number; calls_total: number; truncated: boolean; next_cursor: string | null };
  };
}

export interface GauntletFinding {
  id: string;
  criterion_id: string | null;
  verdict: string;
  severity: string | null;
  location: string | null;
  observed_failure: string | null;
  resolution_condition: string | null;
  next_action: string | null;
  created_at: string;
}

export interface GauntletRound {
  id: string;
  number: number;
  builder_attempt_id: string | null;
  critic_attempt_id: string | null;
  candidate_hash: string | null;
  resolved_criteria: number | null;
  material_progress: boolean | null;
  cost_cents: number | null;
  state: string;
  created_at: string;
  findings: GauntletFinding[];
}

export interface GauntletRun {
  id: string;
  evaluation_contract_id: string;
  evaluation_contract_version: number;
  max_rounds: number;
  state: string;
  stop_reason: string | null;
  created_at: string;
  ended_at: string | null;
  rounds: GauntletRound[];
  totals: { rounds: number; known_cost_cents: number; unknown_cost_rounds: number; resolved_criteria: number };
}

/** GET /v1/tasks/{id}/gauntlet */
export interface GauntletResponse {
  task_id: string;
  runs: GauntletRun[];
  contracts: Record<string, unknown>[];
}

export interface MaestroReview {
  id: string;
  epoch: number | null;
  candidate_hash: string | null;
  verdict: string | null;
  severity: string | null;
  review_model: string | null;
  cost_cents: number | null;
  state: string;
  created_at: string;
}

/** GET /v1/tasks/{id}/maestro-reviews */
export interface MaestroReviewsResponse {
  task_id: string;
  items: MaestroReview[];
  totals: { reviews: number; known_cost_cents: number; unknown_cost_reviews: number };
}

// ---------- observability.ts ----------

export interface TraceNode {
  span_id: string;
  parent_span_id: string | null;
  kind: string;
  name: string;
  attempt_id: string | null;
  runner: string | null;
  role: string | null;
  model: string | null;
  status: string;
  started_at: string;
  ended_at: string | null;
  duration_ms: number;
  open: boolean;
  precision: string;
  children: TraceNode[];
}

export interface TraceAttempt {
  id: string;
  number: number;
  role: string;
  state: string;
  started_at: string;
  ended_at: string | null;
  /** Present since the trace carries them; older builds fall back to /attempts/{id}/backend. */
  supersedes_attempt_id?: string | null;
  end_reason?: string | null;
  agent_profile_id?: string | null;
  model_target_id?: string | null;
}

/** GET /v1/attempts item (listAttemptsPage): attempts across the caller's projects. */
export interface AttemptListItem {
  id: string;
  project_id: string;
  task_id: string;
  task_number: number;
  task_title: string;
  number: number;
  role: string;
  state: string;
  started_at: string;
  ended_at: string | null;
  end_reason: string | null;
  supersedes_attempt_id: string | null;
  agent_profile_id: string | null;
  model_target_id: string | null;
}

export interface AttemptsPage {
  items: AttemptListItem[];
  next_cursor: string | null;
}

export const ATTEMPT_STATES = ["running", "succeeded", "failed", "interrupted", "cancelled", "superseded", "waiting_resource"] as const;

/** GET /v1/routing-configs/{id}/effective-preview: the table if the config were active (read-only). */
export type EffectivePreview = EffectiveConfigView & {
  preview: { routing_config_id: string; scope: "global" | "project"; version: number; state: string; activated: false };
};

/** Coverage of a usage total: every matching call is aggregated (never a silent cut). */
export interface UsageCoverage {
  calls_included: number;
  calls_total: number;
  truncated: boolean;
}

/** GET /v1/tasks/{id}/trace */
export interface TraceResponse {
  task_id: string;
  attempts: TraceAttempt[];
  spans: TraceNode[];
  span_count: number;
  orphan_spans: number;
  notes: string[];
  generated_at: string;
}

/** GET /v1/projects/{id}/usage */
export interface UsageResponse {
  project_id: string;
  period: { from: string | null; to: string | null };
  summary: UsageSummary;
  coverage?: UsageCoverage;
  group_by: string;
  groups: Record<string, UsageSummary>;
  notes: string[];
  generated_at: string;
}

export interface LogRow {
  id: string;
  event_id: string | null;
  level: string;
  service: string;
  message: string;
  code: string | null;
  task_id: string | null;
  attempt_id: string | null;
  trace_id: string | null;
  span_id: string | null;
  correlation_id: string | null;
  fields: Record<string, unknown>;
  truncated: boolean;
  created_at: string;
}

/** GET /v1/projects/{id}/logs (format=json) */
export interface LogsResponse {
  project_id: string;
  items: LogRow[];
  next_cursor: string | null;
  /** F08 (AC-60): latest cutoff applied by the retention purge; the note appears when the range reaches it. */
  detail_expired_before?: string | null;
  note?: string;
}

/** GET/PUT /v1/settings/retention (F08, RF-OBS-18). */
export interface RetentionView {
  detailed_logs: { detailed_logs_days: number; version: number; updated_by: string | null; updated_at: string | null };
  enforcement: string;
  not_enforced: { data_class: string; days: number; note: string }[];
  runs: { id: string; data_class: string; policy_days: number; cutoff: string; rows_deleted: number; started_at: string; finished_at: string }[];
}

export interface LogsQuery {
  level?: string;
  service?: string;
  code?: string;
  task_id?: string;
  search?: string;
  cursor?: string | null;
  limit?: number;
}

// ---------- assistant.ts ----------

export interface AssistantRef {
  type: string;
  id: string;
  href: string;
}

export interface AssistantReply {
  id: string;
  sequence: number;
  kind: "answer" | "command_result" | "explanation_unavailable";
  intent: string;
  text: string;
  data: Record<string, unknown>;
  refs: AssistantRef[];
  source: string;
  model_call: unknown;
  data_as_of: { event_cursor: string; generated_at: string };
}

export interface ConversationMessage {
  id: string;
  session_id: string;
  sequence: number;
  author_role: "user" | "assistant" | "system";
  content: string;
  created_at: string;
}

export interface AssistantMessageResponse {
  session_id: string;
  user_message: ConversationMessage;
  reply: AssistantReply;
  engine_signals?: { task_id: string; engine_signal: "sent" | "unavailable" | string }[];
}

export type AssistantCommand =
  | { type: "create_task"; task: { title: string; description?: string; acceptance_criteria?: string[]; placement?: "backlog" | "prioritized" | "start_now" } }
  | { type: "add_guidance"; task_id: string; text: string }
  | { type: "import_prd"; title: string; content: string };

export interface AssistantSessionsResponse {
  sessions: { id: string; project_id: string | null; role: string; backend: string; created_by: string | null; created_at: string }[];
}

export interface AssistantHistoryResponse {
  session_id: string;
  items: ConversationMessage[];
}

// ---------- infrastructure.ts ----------

export interface SecretState {
  state: "missing" | "active" | "revoked";
  masked_hint: string | null;
  updated_at: string | null;
}

export interface InfraServer {
  id: string;
  name: string;
  purpose: string;
  provider: string | null;
  host: string;
  ssh_port: number;
  ssh_user: string;
  auth_method: "password" | "private_key";
  elevation: "root" | "sudo_nopasswd" | "sudo_password";
  expected_host_fingerprint: string | null;
  observed_host_fingerprint: string | null;
  identity_state: string;
  declared_os: string | null;
  declared_arch: string | null;
  declared_resources: Record<string, unknown>;
  observed_resources: Record<string, unknown> | null;
  workspace_root: string | null;
  autonomy_preset: string;
  state: "draft" | "validated" | "active" | "disabled";
  version: number;
  created_at: string;
  secrets: { password: SecretState; private_key: SecretState; passphrase: SecretState; sudo_password: SecretState };
  policy?: { preset: string; bound_to: Record<string, unknown>; grants: string[]; excluded: string[]; revocable: boolean };
}

export interface CreateServerInput {
  name: string;
  purpose: "development" | "testing";
  provider: string | null;
  host: string;
  ssh_port: number;
  ssh_user: string;
  auth_method: "password" | "private_key";
  password?: string;
  private_key?: string;
  passphrase?: string;
  elevation: "root" | "sudo_nopasswd" | "sudo_password";
  sudo_password?: string;
  expected_host_fingerprint: string | null;
  declared_os: string | null;
  declared_arch: string | null;
  workspace_root: string | null;
  autonomy_preset: string;
}

export interface CoolifyConnection {
  id: string;
  server_id: string;
  base_url: string;
  web_login_url: string | null;
  web_username: string | null;
  authorized_scope: Record<string, unknown>;
  capabilities: Record<string, unknown>;
  state: string;
  version: number;
  created_at: string;
  secrets: { api_token: SecretState; web_password: SecretState };
  capabilities_status?: string;
}

export type ServerSecretField = "password" | "private_key" | "passphrase" | "sudo_password";
export type CoolifySecretField = "api_token" | "web_password";

/** PATCH /v1/infrastructure/servers/{id}: only sent keys change (secrets write-only). */
export type PatchServerInput = Partial<Omit<CreateServerInput, "name">> & { name?: string; expected_version: number };

export interface PatchCoolifyInput {
  expected_version: number;
  base_url?: string;
  api_token?: string;
  web_login_url?: string | null;
  web_username?: string | null;
  web_password?: string;
}

export interface CreateCoolifyInput {
  server_id: string;
  base_url: string;
  api_token?: string;
  web_login_url: string | null;
  web_username: string | null;
  web_password?: string;
}

// ---------- Reports (F08, AC-52, AC-59) ----------

export interface ReportWindow {
  group: string;
  sample: number;
  measured: number;
  completeness: number;
  p50_ms: number | null;
  p95_ms: number | null;
  sufficient: boolean;
}
export interface BottleneckReport {
  project_id: string;
  group_by: "step" | "model" | "role";
  min_sample: number;
  groups: { group: string; before: ReportWindow | null; after: ReportWindow | null; p50_delta_ms: number | null; comparable: boolean }[];
  conditions: { same_models: boolean; same_task_mix: boolean };
  /** AC-59: quality and cost per window (unknown costs counted apart). */
  quality_cost: { before: WindowQualityCost; after: WindowQualityCost } | null;
  note: string;
}
export interface WindowQualityCost {
  attempts: number;
  succeeded: number;
  success_rate: number | null;
  gates_passed: number;
  gates_failed: number;
  gate_pass_rate: number | null;
  calls: number;
  known_cost_cents: number;
  unknown_cost_calls: number;
  cost_complete: boolean;
}
export interface ReviewModeReport {
  project_id: string;
  rows: {
    task_class: string;
    mode: "conventional" | "gauntlet";
    tasks: number;
    accepted: number;
    first_round_acceptance: number | null;
    avg_review_rounds: number | null;
    rework_attempts: number;
    stagnated: number;
    regressions: number | null;
    cost: { per_accepted_cents: number | null; tasks_with_unknown_cost: number };
  }[];
  classes: { task_class: string; comparable: boolean; reason: string | null }[];
  note: string;
}
